<script setup lang="ts">
  defineOptions({
    name: 'ConversationListItem',
  })

  import { computed, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { dayjs } from '@common/core-layer/utils/dayjs'

  import CPAvatar from '@ui/components/CPAvatar.vue'

  import CPText from '@ui/components/CPText.vue'

  import { Microphone01Icon } from '@gohighlevel/ghl-icons/24/outline'

  import { formatVoiceDuration } from '@common/media-layer/utils/voice-duration'
  import { isInlineAudioShape } from '@common/media-layer/utils/voice-note-shape'
  import { MessageSignal } from '../../types/chats.enums'

  import { getConversationUnreadCount } from '../../utils/conversation-unread.utils'

  import UnreadBadge from '../atoms/UnreadBadge.vue'

  import type { IConversation } from '../../types/conversation.types'

  const props = defineProps({
    conversation: {
      type: Object as PropType<IConversation>,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    currentUserId: {
      type: String,
      default: '',
    },
  })

  const emit = defineEmits<{
    click: [conversation: IConversation]
  }>()

  const { t } = useI18n()

  // Get the recipient (other user in the conversation)
  const recipient = computed(() => {
    return props.conversation.recepients?.[0]
  })

  // Get initials for avatar fallback
  const initials = computed(() => {
    const name = recipient.value?.fullName || t('chats.user.deletedUser')
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  })

  // Get avatar URL - CPAvatar handles CDN transformation internally
  const avatarUrl = computed(() => {
    return recipient.value?.avatar || ''
  })

  // Get display name
  const displayName = computed(() => {
    return recipient.value?.fullName || t('chats.user.deletedUser')
  })

  const conversationId = computed(() => props.conversation?._id || 'unknown')

  // Voice note from the most recent message, if any. We override the generic
  // "Attachment" preview with a WhatsApp-style "Voice message (m:ss)" label.
  //
  // Data-shape note (don't re-confuse this): the conversations list endpoint
  // (`GET /gokollab/communities/:locationId/conversations`) returns the full
  // `messages[]` array inline, newest-first — so `messages[0]` IS the latest
  // message for every list row (verified against staging). The optional
  // `lastMessagePreview` / `lastMessageType` "thin-client" fields are NOT sent
  // by this backend; they're a forward-compat path. If a future rollout starts
  // sending those instead of inline `messages[]`, this simply falls back to
  // `lastMessagePreview` below (safe degrade, no crash) until the backend
  // preview is made voice-aware.
  const lastVoiceNoteMedia = computed(() => {
    const media = props.conversation.messages?.[0]?.content?.media
    return isInlineAudioShape(media) ? media : null
  })

  const isVoicePreview = computed(() => lastVoiceNoteMedia.value !== null)

  const voicePreviewLabel = computed(() => {
    const duration = lastVoiceNoteMedia.value?.durationSec
    return duration && duration > 0
      ? t('chats.messagePreview.voiceMessageWithDuration', { duration: formatVoiceDuration(duration) })
      : t('chats.messagePreview.voiceMessage')
  })

  const messagePreview = computed(() => {
    // Voice notes win over the backend preview so they don't read as "Attachment".
    if (isVoicePreview.value) {
      return voicePreviewLabel.value
    }

    // Use backend-computed preview if available (thin client)
    if (props.conversation.lastMessagePreview) {
      return stripHtml(props.conversation.lastMessagePreview)
    }

    // Fallback: compute on frontend
    const lastMessage = props.conversation.messages?.[0]
    if (!lastMessage) return ''

    if (lastMessage.content?.text) {
      return stripHtml(lastMessage.content.text)
    } else if (lastMessage.content?.signal === MessageSignal.TYPING) {
      return t('chats.messagePreview.typing')
    } else if (lastMessage.content?.media?.type === 'gif') {
      return t('chats.messagePreview.sentGif')
    } else if (lastMessage.content?.media) {
      return t('chats.messagePreview.attachment')
    }
    return ''
  })

  // Get relative time (using backend-computed value if available)
  const relativeTimeDisplay = computed(() => {
    // Use backend-computed time if available (thin client)
    if (props.conversation.lastMessageRelativeTime) {
      return props.conversation.lastMessageRelativeTime
    }

    // Fallback: compute on frontend using dayjs
    const lastMessage = props.conversation.messages?.[0]
    if (!lastMessage?.createdAt) return ''

    const messageDate = dayjs(lastMessage.createdAt)
    const now = dayjs()
    const diffMins = now.diff(messageDate, 'minute')
    const diffDays = now.diff(messageDate, 'day')

    // Use short format for recent messages
    if (diffMins < 1) return t('chats.messagePreview.now')
    if (diffDays < 7) return messageDate.fromNow(true) // "2 hours", "3 days" (without "ago")

    // Use date format for older messages
    return messageDate.format('MMM D')
  })

  const unreadCount = computed(() => {
    return getConversationUnreadCount(props.conversation, props.currentUserId)
  })

  const handleClick = () => {
    emit('click', props.conversation)
  }

  function stripHtml(value: string) {
    return value
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }
</script>

<template>
  <div
    :id="`conversation-list-item-${conversationId}`"
    role="button"
    tabindex="0"
    class="flex w-full cursor-pointer items-center gap-3 rounded-lg px-4 py-3 transition-colors"
    :class="[isSelected ? 'bg-hover' : 'hover:bg-hover active:bg-hover']"
    @click="handleClick"
    @keyup.enter="handleClick"
  >
    <!-- Avatar -->
    <div class="relative flex-shrink-0">
      <CPAvatar :src="avatarUrl" size="md" :title="displayName" class="bg-border">
        <template v-if="!avatarUrl" #fallback>
          <span class="text-sm font-medium text-secondary">{{ initials }}</span>
        </template>
      </CPAvatar>
    </div>

    <!-- Content -->
    <div class="min-w-0 flex-1">
      <div class="flex items-center justify-between gap-2">
        <!-- Name -->
        <CPText size="sm" weight="semibold" class="truncate text-body">
          {{ displayName }}
        </CPText>

        <!-- Time -->
        <CPText size="xs" class="flex-shrink-0 text-600">
          {{ relativeTimeDisplay }}
        </CPText>
      </div>

      <div class="mt-0.5 flex items-center justify-between gap-2">
        <!-- Message preview -->
        <div class="flex min-w-0 flex-1 items-center gap-1">
          <Microphone01Icon v-if="isVoicePreview" class="h-3.5 w-3.5 flex-shrink-0 text-secondary" />
          <CPText size="xs" class="min-w-0 flex-1 truncate text-600">
            {{ messagePreview }}
          </CPText>
        </div>
        <UnreadBadge :id="`conversation-unread-badge-${conversationId}`" :count="unreadCount" />
      </div>
    </div>
  </div>
</template>
